/**
 * CampusHealthLightsCard — Dashboard Pulse Widget.
 * Red / amber / green rollup per campus for attendance, behaviour, follow-ups
 * and unfilled timetable periods. Reads /api/school/tenants/{tid}/health-lights.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LIGHT_COLORS = {
  red: 'bg-rose-500',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
};
const LIGHT_BG = {
  red: 'bg-rose-50 border-rose-200',
  amber: 'bg-amber-50 border-amber-200',
  green: 'bg-emerald-50 border-emerald-200',
};

const METRIC_LABELS = {
  attendance_7d: 'Attendance (7d)',
  behaviour_7d: 'Behaviour (7d)',
  followups_14d: 'Follow-ups (14d)',
  unfilled_periods: 'Unfilled periods',
};

const formatValue = (k, v) => {
  if (k === 'attendance_7d') return v === null || v === undefined ? 'n/a' : `${Math.round(v * 100)}%`;
  return String(v);
};

export default function CampusHealthLightsCard({ tenantId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['campus-health-lights', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}/health-lights`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    enabled: !!tenantId,
    refetchInterval: 60000,
  });

  if (isLoading) {
    return <Card data-testid="health-lights-card"><CardContent className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>;
  }
  const rows = data?.campuses || [];
  if (rows.length <= 1 && (data?.total_campuses || 0) === 0) return null;

  return (
    <Card data-testid="health-lights-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-500" /> Campus Pulse
          <Badge variant="outline" className="ml-2 text-[10px]">Live · refreshes 60s</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${LIGHT_COLORS.green}`} /> Green — healthy</span>
          <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${LIGHT_COLORS.amber}`} /> Amber — watch</span>
          <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${LIGHT_COLORS.red}`} /> Red — act now</span>
        </div>
        <div className="grid gap-2">
          {rows.map((row) => (
            <div key={row.campus_id || 'tenant-wide'}
                 data-testid={`health-row-${row.campus_id || 'tenant'}`}
                 className={`rounded-lg border p-3 ${LIGHT_BG[row.light] || 'bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <span className={`w-2.5 h-2.5 rounded-full ${LIGHT_COLORS[row.light]}`} />
                  {row.campus_name || 'Unnamed campus'}
                </div>
                {row.light === 'green' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-rose-600" />}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {row.metrics.map((m) => (
                  <div key={m.key} className="bg-white/70 rounded px-2 py-1 text-[11px] flex items-center justify-between">
                    <span className="text-slate-500">{METRIC_LABELS[m.key] || m.key}</span>
                    <span className={`font-mono font-semibold ${m.color === 'red' ? 'text-rose-600' : m.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {formatValue(m.key, m.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
